import { Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef, Getter } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostResourcePlannerChange } from '@legacy/apis/resourcePlannerAPI';
import { IResourcePlannerPeriodValueString } from '@legacy/apis/types/resourcePlannerAPI';
import { useGetCurrentLanguage } from '@legacy/apis/userSettingsAPI';
import { useGetLocale } from '@legacy/components/global/LocaleProvider';
import { CircularProgress } from '@legacy/components/mui-components';
import { Deck, THorizontalAlignment } from '@legacy/components/ui-components';
import { useFilterStore } from '@legacy/stores/FilterStore';
import { useResourcePlannerStore } from '@legacy/stores/ResourcePlannerStore/ResourcePlannerStore';
import {
  convertDateToLocaleString,
  getDateStringFromSiteLocale,
  getDateWithZeroOffsetFromDateString,
  getLanguageFromSiteLocale,
} from '@legacy/utils/date';
import { translationAnyText } from '@legacy/utils/translation';
import { TCollapseExpandState } from '../../helper/expandedRow';
import generatePeriodLabel from '../../helper/generatePeriodLabel';
import { getFixedColumnTitleIdentifier } from '../../helper/getFixedColumnTooltip';
import { getTranslatedText } from '../../helper/getTranslatedText';
import { reloadRpTables } from '../../helper/reloadRpTables';
import { useGetProjectView } from '../../hooks';
import { RPRow, RTColumn, RTRow } from '../../types/resourcePlanner';
import ExcessiveOrNoData from '../ExcessiveOrNoData';
import { GeneralColumnRenderer } from '../GeneralColumnRenderer';
import TotalColumnRenderer from '../TotalColumnRenderer';
import { FIXED_COLUMNS, TAKE } from './ResourceTableGroupedByProject.constants';
import styles from './ResourceTableGroupedByProject.module.scss';
import { ResourceTableGroupedByProjectTable } from './ResourceTableGroupedByProjectTable';

export const ResourceTableGroupedByProject = () => {
  const { t } = useTranslation('resourcePlanner');
  const qc = useQueryClient();

  const { currentLanguage } = useGetCurrentLanguage();
  const siteLocale = useGetLocale();
  const siteLanguage = getLanguageFromSiteLocale(siteLocale);

  const { filterQueryObj } = useFilterStore();
  const filterQueryObjString = useRef<string>(JSON.stringify(filterQueryObj));
  const { selectedViewOptions } = useResourcePlannerStore();

  const [expandedRows, setExpandedRows] = useState<TCollapseExpandState[]>([]);

  const {
    children,
    fetchNextPage,
    isEmpty,
    isFetching,
    isFetchingNextPage,
    isNextPageAvailable,
    isRefetching,
    partial,
    periods,
    responseType,
  } = useGetProjectView({
    expandedRows,
    params: selectedViewOptions,
    selectedFilterList: filterQueryObj,
    take: TAKE,
  });

  const { mutate: postChange } = usePostResourcePlannerChange();
  const unitType = selectedViewOptions['unit-types'];
  const reportingType = selectedViewOptions['reporting-types'];

  const columns = useMemo<ColumnDef<RPRow>[]>(
    () => [
      {
        accessorKey: 'name',
        cell: ({ getValue }: { getValue: Getter<string> }) => getValue(),
        header: () => t('ColumnLabelName'),
        id: 'name',
        size: 500,
      },
      ...FIXED_COLUMNS.map((fixedColumn) => ({
        accessorKey: fixedColumn.identifier,
        cell: ({
          row,
          column,
          getValue,
        }: {
          row: RTRow;
          column: RTColumn;
          getValue: Getter<string>;
        }) => {
          const cellValue = getValue();

          const value =
            column.columnDef.meta?.format === 'date' && cellValue !== undefined
              ? convertDateToLocaleString(new Date(cellValue), siteLocale)
              : cellValue;

          const cellDisplayValue = GeneralColumnRenderer({
            row,
            id: column.columnDef.id,
            format: column.columnDef.meta?.format,
            value,
          });

          return cellDisplayValue;
        },
        header: () =>
          getTranslatedText(t, unitType, fixedColumn.identifier, 'ProjectView'),
        id: fixedColumn.identifier,
        meta: {
          alignment: fixedColumn?.alignment,
          dividerBorderLeft: fixedColumn.identifier === 'budget',
          format: fixedColumn?.format,
          title: translationAnyText(
            t,
            getFixedColumnTitleIdentifier(fixedColumn.identifier),
          ),
        },
        size: fixedColumn.size,
      })),
      ...periods.map((periodColumn, index) => ({
        accessorFn: (row: RPRow) => row.values[periodColumn.identifier],
        accessorKey: periodColumn.identifier,
        cell: ({
          getValue,
        }: {
          getValue: Getter<IResourcePlannerPeriodValueString>;
        }) => getValue().displayValue,
        id: `periodCol_${periodColumn.identifier}`,
        header: () =>
          generatePeriodLabel(
            periodColumn.identifier,
            selectedViewOptions['period-types'],
            currentLanguage,
            t,
          ),
        meta: {
          alignment: 'right' as THorizontalAlignment,
          dividerBorderLeft: index === 0,
          editable: true,
          endsAt: periodColumn.endsAt,
          isPeriod: true,
          periodIndex: index,
          startsAt: periodColumn.startsAt,
          title:
            selectedViewOptions['period-types'] !== 'day'
              ? `${getDateStringFromSiteLocale(
                  getDateWithZeroOffsetFromDateString(periodColumn.startsAt),
                  siteLanguage,
                )} - ${getDateStringFromSiteLocale(
                  getDateWithZeroOffsetFromDateString(periodColumn.endsAt),
                  siteLanguage,
                )}`
              : '',
        },
        size: 100,
      })),
      {
        id: 'total',
        accessorKey: 'total',
        meta: {
          type: 'total',
          alignment: 'right' as THorizontalAlignment,
        },
        header: () => t('ColumnLabelTotal'),
        cell: ({ row }) =>
          TotalColumnRenderer({
            displayValue: row.original.total?.displayValue,
            unitType,
          }),
      },
    ],
    [
      selectedViewOptions,
      periods,
      t,
      currentLanguage,
      unitType,
      siteLocale,
      siteLanguage,
    ],
  );

  const onCellValueChange = useCallback(
    (row: RTRow, column: RTColumn, value: string) => {
      const { resourceId, workItemId } = row.original;
      const startsAt = column.columnDef.meta?.startsAt!;
      const endsAt = column.columnDef.meta?.endsAt!;

      postChange(
        {
          resourceId,
          workItemId,
          unitType,
          value,
          startsAt,
          endsAt,
        },
        {
          onSettled: () => {
            reloadRpTables(qc);
          },
        },
      );
    },
    [postChange, qc, unitType],
  );

  const handleOnToggleRow = useCallback(
    (row: RTRow) => {
      const toggledRow = expandedRows.find(
        (item) => item.tableRowId === row.id,
      );
      setExpandedRows((prev) => {
        if (toggledRow) {
          toggledRow.isExpanded = !row.getIsExpanded();
          return prev.filter((r) => r.isExpanded);
        }
        return [
          ...prev,
          {
            canExpand: row.original.canExpand,
            isExpanded: !row.getIsExpanded(),
            originalId: row.original.sourceReferenceId,
            tableRowId: row.id,
            type: row.original.type,
          },
        ];
      });
      row.toggleExpanded();
    },
    [expandedRows],
  );

  const rowsThatAreLoading = useMemo(
    () =>
      partial
        .filter((item) => item.isFetching)
        .map((item) => `${item.originalId}_0`),
    [partial],
  );

  useEffect(() => {
    const stringedFilterQueryObj = JSON.stringify(filterQueryObj);
    if (filterQueryObjString.current === stringedFilterQueryObj) {
      return;
    }
    filterQueryObjString.current = JSON.stringify(filterQueryObj);
    setExpandedRows([]);
  }, [filterQueryObj]);

  if (
    responseType === 'ExcessiveData' ||
    (isEmpty && responseType === 'NoData')
  ) {
    return <ExcessiveOrNoData type={responseType} />;
  }

  return (
    <>
      <Deck.Item hasOverflow>
        <ResourceTableGroupedByProjectTable
          columns={columns}
          data={children}
          fetchNextPage={
            !isFetching && isNextPageAvailable ? fetchNextPage : () => {}
          }
          handleOnToggleRow={handleOnToggleRow}
          onCellValueChange={onCellValueChange}
          reportingType={reportingType}
          rowsThatAreLoading={rowsThatAreLoading}
          selectedViewOptions={selectedViewOptions}
          unitType={unitType}
        />
      </Deck.Item>
      {isFetchingNextPage ? (
        <Deck.Item>
          <Stack className={styles.fetchNextPageSpinner}>
            <CircularProgress />
          </Stack>
        </Deck.Item>
      ) : null}
      {(isFetching || isRefetching) && !isFetchingNextPage ? (
        <Deck.Item>
          <Stack className={styles.initialfetchSpinner}>
            <CircularProgress />
          </Stack>
        </Deck.Item>
      ) : null}
    </>
  );
};
